import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  InputGroup,
  Label,
  LoadingSpinner,
  Modal,
  RadioButton,
  RadioGroup,
  ScrollShadow,
  Switch,
  Text
} from '@opengov/component-library/capital';
import { DEFAULT_STORY_MARGINS, EShowHeaderFooter } from '@opengov/stories-shared/constants';
import { IStoryMargins } from '@opengov/stories-shared/types';
import { debounce } from 'lodash';
import React, { createRef } from 'react';

import { parseMarginValue } from '../../../../modals/components/ExportPdfOptions/ExportPdfOptions';
import {
  IExportPdfRequest,
  IModalMessages,
  IPreviewPdfFolderRequest,
  IPreviewPdfProps,
  IPublicationModalMessages
} from '../../../../types';

import { FOLDER, MAX_STORY_ERROR, PUBLICATION, SECTION } from '../../../constants';

import styles from './ExportPdfModal.scss';

const SCROLL_HEIGHT = '55vh';
export interface IExportPdfModalProps {
  messages: IModalMessages;
  storyMarginsEnabled: boolean;
  isPreviewingPdfFailure: boolean;
  isPreviewingPdf: boolean;
  previewFolderPdfError: string;
  isPublicationSectionExport?: boolean;
  isPublicationExport?: boolean;
  folderId?: string;
  publicationId?: string;
  publicationModalMessages?: IPublicationModalMessages;
  onClose(): void;
  getPdfPreviewLink(props: IPreviewPdfFolderRequest): Promise<string>;
  exportFolderPdf?(props: IExportPdfRequest): Promise<unknown>;
  // Publication-level export is driven by `useExportPublicationPdf`
  // in the parent (this is a class component). Required when isPublicationExport.
  exportPublicationPdf?(props: { publicationId: string; pdfOptions: Record<string, unknown> }): Promise<unknown>;
}

const NO_STORIES = 'NO_STORIES';
export interface IExportPdfModalState {
  displayTitle: boolean;
  displayDate: boolean;
  displayPageNumber: boolean;
  enlargeFonts: boolean;
  pdfPreviewLink: string;
  margins: IStoryMargins;
  isExportingPdf: boolean;
  pdfLink: string;
  showStoriesHeader: string;
  showStoriesFooter: string;
  includeStoryStates: string[];
}

const exportPdfFailureMessage = (exportedStoriesCount: number, exportLevel: string): JSX.Element => {
  return (
    <Text as="div" className={styles.pdfFailureMessage} dataTest="pdf-failure-message">
      <Text as="div" size="small" className={styles.pdfFailureMessageMargin} color="light">
        {exportLevel === PUBLICATION
          ? `This publication has ${exportedStoriesCount} stories. ${MAX_STORY_ERROR}`
          : `The selected ${exportLevel} & sub-${exportLevel}s have ${exportedStoriesCount} stories. ${MAX_STORY_ERROR}`}
      </Text>
      <Text as="div" size="small" className={styles.pdfFailureMessageMargin} color="light">
        {`Please organize the stories into two or more ${exportLevel}s and export each ${exportLevel} separately.`}
      </Text>
    </Text>
  );
};
export class ExportPdfModal extends React.Component<IExportPdfModalProps, IExportPdfModalState> {
  controllerRef: React.MutableRefObject<AbortController>;

  generatePreview = debounce(
    (options?: Partial<IPreviewPdfProps>): void => {
      const { getPdfPreviewLink, folderId, publicationId = '', isPublicationExport = false } = this.props;
      const {
        displayTitle,
        displayDate,
        displayPageNumber,
        enlargeFonts,
        margins,
        showStoriesHeader,
        showStoriesFooter,
        includeStoryStates
      } = this.state;
      const { marginBottom, marginLeft, marginRight, marginTop } = margins;
      const showMargins = !!marginBottom || !!marginLeft || !!marginRight || !!marginTop;

      if (this.controllerRef.current) {
        this.controllerRef.current.abort();
      }

      const controller = new AbortController();
      this.controllerRef.current = controller;

      const previewLinkArgs = {
        ...(!!isPublicationExport ? { publicationId } : { folderId }),
        pdfOptions: {
          displayTitle,
          displayDate,
          displayPageNumber,
          enlargeFonts,
          margins,
          showMargins,
          template: false,
          showStoriesHeader,
          showStoriesFooter,
          includeStoryStates: encodeURI(JSON.stringify(includeStoryStates)),
          ...options
        },
        controller: this.controllerRef.current
      };
      void getPdfPreviewLink(previewLinkArgs).then((res) => {
        this.setState({ pdfLink: res });
      });
    },
    1000,
    { trailing: true }
  );
  constructor(props: IExportPdfModalProps) {
    super(props);

    this.controllerRef = createRef<AbortController | null>();
    this.controllerRef.current = new AbortController();

    this.state = {
      displayTitle: false,
      displayDate: false,
      displayPageNumber: false,
      enlargeFonts: false,
      pdfPreviewLink: '',
      margins: { ...DEFAULT_STORY_MARGINS },
      isExportingPdf: false,
      pdfLink: null,
      showStoriesHeader: EShowHeaderFooter.All,
      showStoriesFooter: EShowHeaderFooter.All,
      includeStoryStates: ['published']
    };
  }

  onDisplayDateToggle = () => {
    this.generatePreview({ displayDate: !this.state.displayDate });
    this.setState({ displayDate: !this.state.displayDate });
  };

  componentDidMount() {
    this.generatePreview({
      margins: this.state.margins
    });
  }

  onDisplayPageNumberToggle = () => {
    const { displayPageNumber } = this.state;
    const newDisplayPageNumber = !displayPageNumber;
    this.generatePreview({ displayPageNumber: newDisplayPageNumber });
    this.setState({
      displayPageNumber: newDisplayPageNumber
    });
  };

  onDisplayTitleToggle = () => {
    this.generatePreview({ displayTitle: !this.state.displayTitle });
    this.setState({ displayTitle: !this.state.displayTitle });
  };

  onEnlargeFontsToggle = () => {
    this.generatePreview({ enlargeFonts: !this.state.enlargeFonts });
    this.setState({ enlargeFonts: !this.state.enlargeFonts });
  };

  getPdfOptions = () => {
    const {
      displayTitle,
      displayDate,
      displayPageNumber,
      enlargeFonts,
      margins,
      showStoriesHeader,
      showStoriesFooter,
      includeStoryStates
    } = this.state;
    const { marginBottom, marginLeft, marginRight, marginTop } = margins;
    const showMargins = !!marginBottom || !!marginLeft || !!marginRight || !!marginTop;
    return {
      display_title: displayTitle,
      display_date: displayDate,
      display_page_number: displayPageNumber,
      enlarge_fonts: enlargeFonts,
      margins: encodeURIComponent(JSON.stringify(margins)),
      show_margins: showMargins,
      show_stories_header: showStoriesHeader,
      show_stories_footer: showStoriesFooter,
      include_story_states: encodeURIComponent(JSON.stringify(includeStoryStates))
    };
  };

  handleSubmit = async () => {
    if (this.controllerRef.current) {
      this.controllerRef.current.abort();
    }
    const {
      onClose,
      folderId,
      isPublicationSectionExport = false,
      isPublicationExport = false,
      publicationId,
      exportPublicationPdf,
      exportFolderPdf
    } = this.props;
    this.setState({
      isExportingPdf: true
    });

    isPublicationExport
      ? await exportPublicationPdf({
          publicationId,
          pdfOptions: this.getPdfOptions()
        }).then(() => {
          this.setState({
            isExportingPdf: false
          });
          onClose();
        })
      : await exportFolderPdf?.({
          isPublicationSectionExport,
          folderId,
          pdfOptions: this.getPdfOptions()
        }).then(() => {
          this.setState({
            isExportingPdf: false
          });
          onClose();
        });
  };

  handleUpdateMargins = (key: keyof IStoryMargins, value: string) => {
    const updatedMargins = { ...this.state.margins, [key]: parseMarginValue(value) };
    this.generatePreview();
    this.setState({
      margins: updatedMargins
    });
  };

  handleHeaderOption = (value: string) => {
    this.generatePreview({ showStoriesHeader: value });
    this.setState({ showStoriesHeader: value });
  };

  handleFooterOption = (value: string) => {
    this.generatePreview({ showStoriesFooter: value });
    this.setState({ showStoriesFooter: value });
  };

  handleIncludeStories = (e) => {
    let includeStories = this.state.includeStoryStates;
    if (e.target.checked) {
      includeStories.push(e.target.id);
      this.setState({ includeStoryStates: includeStories });
    } else {
      includeStories = this.state.includeStoryStates.filter((story) => {
        return story !== e.target.id;
      });
      this.setState({
        includeStoryStates: includeStories
      });
    }
    this.generatePreview({ includeStoryStates: encodeURI(JSON.stringify(includeStories)) });
  };

  renderMarginsSection = () => {
    const { messages } = this.props;
    const { margins } = this.state;
    return (
      <div className={styles.marginsSection} data-test="story-export-margins">
        <Label variant="group" className={styles.title}>
          {messages.adjustPageMargins}
        </Label>
        <div className={styles.spacer} />
        <Form.Row>
          <Form.Item label={messages.topMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginTop}`}
                dataTest="story-export-top-margin"
                value={margins.marginTop ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginTop', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
          <div className={styles.inlineSpacer} />
          <Form.Item label={messages.bottomMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginBottom}`}
                dataTest="story-export-bottom-margin"
                value={margins.marginBottom ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginBottom', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
        </Form.Row>
        <div className={styles.spacer} />
        <Form.Row>
          <Form.Item label={messages.leftMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginLeft}`}
                dataTest="story-export-left-margin"
                value={margins.marginLeft ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginLeft', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
          <div className={styles.inlineSpacer} />
          <Form.Item label={messages.rightMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginRight}`}
                dataTest="story-export-right-margin"
                value={margins.marginRight ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginRight', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
        </Form.Row>
      </div>
    );
  };

  render() {
    const {
      onClose,
      messages,
      storyMarginsEnabled,
      isPreviewingPdfFailure,
      isPreviewingPdf,
      previewFolderPdfError,
      isPublicationExport,
      publicationModalMessages,
      isPublicationSectionExport
    } = this.props;
    const {
      displayTitle,
      displayDate,
      displayPageNumber,
      enlargeFonts,
      isExportingPdf,
      pdfLink,
      showStoriesHeader,
      showStoriesFooter
    } = this.state;
    const {
      showHeaderName,
      showFooterName,
      headerFooterForAllStory,
      headerFooterForFirstStory,
      headerFooterForLastStory,
      headerFooterForBothStory,
      headerFooterForNoStory
    } = messages;
    let errMessage = '';
    let errorMessageItem = null;
    let exportTitle = '';

    if (isPublicationSectionExport) {
      exportTitle = publicationModalMessages?.sectionTitle;
    } else if (isPublicationExport) {
      exportTitle = publicationModalMessages?.publicationTitle;
    } else {
      exportTitle = messages?.folderTitle;
    }
    if (isPreviewingPdfFailure) {
      let errorMsg = null;
      try {
        errorMsg = JSON.parse(previewFolderPdfError);
      } catch {
        errorMsg = null;
      }

      if (errorMsg?.msg === 'STORIES_EXPORT_LIMIT_REACHED') {
        errorMessageItem = exportPdfFailureMessage(errorMsg?.counts.stories, FOLDER);
        if (isPublicationExport) {
          errorMessageItem = exportPdfFailureMessage(errorMsg?.counts.stories, PUBLICATION);
        } else if (isPublicationSectionExport) {
          errorMessageItem = exportPdfFailureMessage(errorMsg?.counts.stories, SECTION);
        }
      } else {
        if (isPublicationExport) {
          errMessage =
            errorMsg?.msg === NO_STORIES
              ? publicationModalMessages.noStoriesFoundInPublication
              : messages.pdfPreviewFailureText;
        } else if (isPublicationSectionExport) {
          errMessage =
            errorMsg?.msg === NO_STORIES
              ? publicationModalMessages.noStoriesFoundInSection
              : messages.pdfPreviewFailureText;
        } else {
          errMessage = errorMsg?.msg === NO_STORIES ? messages.noStoriesFound : messages.pdfPreviewFailureText;
        }

        errorMessageItem = (
          <Text className={styles.pdfFailureMessage} dataTest="pdf-failure-message">
            {errMessage}
          </Text>
        );
      }
    }
    return (
      <div data-test="export-pdf-modal">
        <Modal.Work width="wide" title={messages.pdfExport} onClose={onClose}>
          <div className={styles.container}>
            {/* Form section */}
            <ScrollShadow height={SCROLL_HEIGHT} backgroundColor="gray-100">
              <div className={styles.formContainer}>
                <section>
                  <Label variant="group" className={styles.title}>
                    {messages.exportStoryTitle}
                  </Label>
                  <ul>
                    <li>
                      <Checkbox.Small
                        label="All Public stories"
                        checked={true}
                        onChange={this.handleIncludeStories}
                        className={styles.checkbox}
                        id="published"
                        dataTest="all-public-stories"
                      />
                    </li>
                    <li>
                      <Checkbox.Small
                        label="All Organization stories"
                        checked={false}
                        onChange={this.handleIncludeStories}
                        className={styles.checkbox}
                        id="internalPublished"
                        dataTest="all-organization-stories"
                      />
                    </li>
                    <li>
                      <Checkbox.Small
                        label="All Draft stories"
                        checked={false}
                        onChange={this.handleIncludeStories}
                        className={styles.checkbox}
                        id="draft"
                        dataTest="draft-stories"
                      />
                    </li>
                  </ul>
                  <div className={styles.divider}>
                    <Divider.Horizontal />
                  </div>
                </section>
                <Accordion title={messages.showHeaderTitle} dataTest="show-header">
                  <section>
                    <RadioGroup
                      onChange={(value) => this.handleHeaderOption(value)}
                      value={showStoriesHeader}
                      dataTest="show-header-options"
                    >
                      <RadioButton.Small
                        name={showHeaderName}
                        label={headerFooterForAllStory}
                        value={EShowHeaderFooter.All}
                        dataTest="show-all-header"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showHeaderName}
                        label={headerFooterForFirstStory}
                        value={EShowHeaderFooter.First}
                        dataTest="show-first-header"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showHeaderName}
                        label={headerFooterForLastStory}
                        value={EShowHeaderFooter.Last}
                        dataTest="show-last-header"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showHeaderName}
                        label={headerFooterForBothStory}
                        value={EShowHeaderFooter.Both}
                        dataTest="show-first-last-header"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showHeaderName}
                        label={headerFooterForNoStory}
                        value={EShowHeaderFooter.None}
                        dataTest="show-no-header"
                        className={styles.showHeaderFooterOptions}
                      />
                    </RadioGroup>
                  </section>
                </Accordion>
                <div className={styles.divider}>
                  <Divider.Horizontal />
                </div>
                <Accordion title={messages.showFooterTitle} dataTest="show-footer">
                  <section>
                    <RadioGroup
                      onChange={(value) => this.handleFooterOption(value)}
                      value={showStoriesFooter}
                      dataTest="show-footer-options"
                    >
                      <RadioButton.Small
                        name={showFooterName}
                        label={headerFooterForAllStory}
                        value={EShowHeaderFooter.All}
                        dataTest="show-all-footer"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showFooterName}
                        label={headerFooterForFirstStory}
                        value={EShowHeaderFooter.First}
                        dataTest="show-first-footer"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showFooterName}
                        label={headerFooterForLastStory}
                        value={EShowHeaderFooter.Last}
                        dataTest="show-last-footer"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showFooterName}
                        label={headerFooterForBothStory}
                        value={EShowHeaderFooter.Both}
                        dataTest="show-first-last-footer"
                        className={styles.showHeaderFooterOptions}
                      />
                      <RadioButton.Small
                        name={showFooterName}
                        label={headerFooterForNoStory}
                        value={EShowHeaderFooter.None}
                        dataTest="show-no-footer"
                        className={styles.showHeaderFooterOptions}
                      />
                    </RadioGroup>
                  </section>
                </Accordion>
                <div className={styles.divider}>
                  <Divider.Horizontal />
                </div>
                <Label variant="group" className={styles.title}>
                  {messages.fonts}
                </Label>
                <div className={styles.toggle}>
                  <Switch.Small
                    id="toggle-enlarge-fonts"
                    dataTest="toggle-enlarge-fonts"
                    label={messages.enlargeSizes}
                    checked={enlargeFonts}
                    onChange={this.onEnlargeFontsToggle}
                  />
                </div>
                <div className={styles.divider}>
                  <Divider.Horizontal />
                </div>
                <Label variant="group" className={styles.title}>
                  {messages.pageFootnotes}
                </Label>
                <div className={styles.toggle}>
                  <Switch.Small
                    id="toggle-title"
                    dataTest="toggle-title"
                    label={exportTitle}
                    checked={displayTitle}
                    onChange={this.onDisplayTitleToggle}
                  />
                </div>
                <div className={styles.toggle}>
                  <Switch.Small
                    id="toggle-date"
                    dataTest="toggle-date"
                    label={messages.dateOfExport}
                    checked={displayDate}
                    onChange={this.onDisplayDateToggle}
                  />
                </div>
                <div className={styles.toggle}>
                  <Switch.Small
                    id="toggle-page-number"
                    dataTest="toggle-page-number"
                    label={messages.pageNumber}
                    checked={displayPageNumber}
                    onChange={this.onDisplayPageNumberToggle}
                  />
                </div>
                <div className={styles.divider}>
                  <Divider.Horizontal />
                </div>
                {storyMarginsEnabled && this.renderMarginsSection()}
              </div>
            </ScrollShadow>
            <div className={styles.divider}>
              <Divider.Vertical />
            </div>

            {/* Preview section  */}
            <div className={styles.previewParentContainer}>
              <div className={styles.previewContainer}>
                <Label variant="h2">{messages.preview}</Label>
                <div id="pdfPreview" className={styles.iframeContainer}>
                  {isPreviewingPdf && (
                    <div className={styles.loader} data-test="story-exporting-pdf">
                      <LoadingSpinner dataTest="pdf-preview-loader" message={messages.pdfPreviewText} variant="page" />
                    </div>
                  )}

                  <div className={styles.formContainer}>
                    {pdfLink && !isPreviewingPdf && !isPreviewingPdfFailure && (
                      <iframe className={styles.preview} width="100%" height="98%" src={pdfLink} name="story" />
                    )}
                    {isPreviewingPdfFailure && errorMessageItem}
                  </div>
                </div>
              </div>
              <div className={styles.previewNote}>
                {isPublicationExport || isPublicationSectionExport
                  ? publicationModalMessages?.pdfPreviewNote
                  : messages.pdfPreviewNote}
              </div>
            </div>
          </div>

          <div className={styles.bottomContainer}>
            <Button onClick={onClose} dataTest="export-pdf-modal-button-cancel">
              {messages.cancel}
            </Button>
            <Button
              loading={isExportingPdf}
              variant="primary"
              onClick={this.handleSubmit}
              dataTest="export-pdf-modal-button-export"
              disabled={isPreviewingPdfFailure ? true : false}
            >
              {messages.export}
            </Button>
          </div>
        </Modal.Work>
      </div>
    );
  }
}
